/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin-risks-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { orderBy } from '@progress/kendo-data-query';
import React, { useState, useEffect, useMemo } from 'react';
import { LibraryRiskFieldsExtended } from '../../../interfaces/library-risk.interface';
import ExpandableGridV2 from '../core/expandable/grid-v2';
import { GridColumnDateFilter, GridColumnMenuActiveFilter } from '../core/grid-filters';
import { ExtraMenuItem } from '../core/grid-header/grid-menu/grid-menu.interface';
import AdminControlsChildGrid from './admin-controls-child-grid';
import { useTranslation, useGridCells } from 'hooks';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { getCodes } from 'country-list';

const AdminRisksGrid = ({
    title,
    isChild,
    data,
    rowActions,
    showPager,
    height,
    showDetail,
    editControl,
    unassignControl,
    isSystemLevel,
    canEdit,
    extraMenuItems,
    className,
}: {
    title?: string;
    isChild?: boolean;
    data: LibraryRiskFieldsExtended[];
    rowActions: any;
    showPager: boolean;
    height?: number | string;
    showDetail: boolean;
    editControl?: Function;
    unassignControl?: Function;
    isSystemLevel: boolean;
    canEdit?: boolean;
    extraMenuItems?: ExtraMenuItem[];
    className?: string;
}) => {
    const { t } = useTranslation();
    const { YesNoCell, DateCell, MaxLengthCell, CustomizableCell, ActiveInactiveCell } = useGridCells();
    const [items, setItems] = useState(null);
    const countryCodes = useMemo(() => getCodes(), []);

    useEffect(() => {
        const newData = restructuredUpdatedCreatedBy(data);
        setItems(
            orderBy(newData, [
                { field: 'displayOrder', dir: 'asc' },
                { field: 'active', dir: 'desc' },
                { field: 'updated', dir: 'desc' },
            ]),
        );
    }, [data]);

    const columns: any = [
        ...(isSystemLevel
            ? // System level columns (parent and child)
              [
                  ...(!isChild
                      ? [
                            {
                                label: t('ra:controls'),
                                field: 'numberOfControls',
                                id: 'risk-controls',
                                filter: 'numeric',
                                minWidth: 150,
                                lockedByDefault: true,
                            },
                        ]
                      : []),
                  {
                      label: t('ra:statement'),
                      field: 'statement',
                      id: 'risk-statement',
                      minWidth: 180,
                      lockedByDefault: true,
                      cells: { data: MaxLengthCell(150) },
                  },
                  {
                      label: t('active'),
                      id: 'risk-active',
                      field: 'active',
                      minWidth: 140,
                      cells: { data: YesNoCell },
                      filterUi: GridColumnMenuActiveFilter,
                  },
              ]
            : // Org level columns (parent and child)
              [
                  ...(!isChild
                      ? [
                            {
                                label: t('ra:Order'),
                                field: 'displayOrder',
                                id: 'risk-order',
                                filter: 'numeric',
                                minWidth: 150,
                                lockedByDefault: true,
                            },
                            {
                                label: t('ra:controls'),
                                field: 'numberOfControls',
                                id: 'risk-controls',
                                filter: 'numeric',
                                minWidth: 150,
                                lockedByDefault: true,
                            },
                            {
                                label: t('active'),
                                id: 'risk-active',
                                field: 'active',
                                minWidth: 140,
                                lockedByDefault: true,
                                cells: { data: YesNoCell },
                                filterUi: GridColumnMenuActiveFilter,
                            },
                        ]
                      : []),
                  {
                      label: t('ra:statement'),
                      field: 'statement',
                      id: 'risk-statement',
                      minWidth: 180,
                      lockedByDefault: true,
                      cells: { data: MaxLengthCell(150) },
                  },
                  {
                      label: t('admin:riskLibraryCategory'),
                      id: 'risk-library-category',
                      minWidth: 220,
                      field: 'riskLibraryCategory',
                      cells: {
                          data: ActiveInactiveCell(
                              (dataItem: any) => dataItem?.active === false,
                              (dataItem: any) => dataItem?.name,
                              true,
                              'riskLibraryCategories',
                          ),
                      },
                  },
                  {
                      label: t('admin:riskCategory'),
                      id: 'risk-category',
                      minWidth: 220,
                      field: 'riskCategory.name',
                      cells: {
                          data: ActiveInactiveCell(
                              (dataItem: any) => dataItem.riskCategory?.active === false,
                              (dataItem: any) => dataItem.riskCategory?.name,
                          ),
                      },
                  },
                  {
                      label: t('ra:controlStrategy'),
                      id: 'control-strategy',
                      minWidth: 230,
                      field: 'controlStrategy.name',
                      cells: {
                          data: ActiveInactiveCell(
                              (dataItem: any) => dataItem.controlStrategy?.active === false,
                              (dataItem: any) => dataItem.controlStrategy?.name,
                          ),
                      },
                  },
                  {
                      label: t('ra:responsibleFunction'),
                      id: 'control-responsible-func',
                      minWidth: 260,
                      field: 'responsibleFunction.name',
                      cells: {
                          data: ActiveInactiveCell(
                              (dataItem: any) => dataItem.responsibleFunction?.active === false,
                              (dataItem: any) => dataItem.responsibleFunction?.name,
                          ),
                      },
                  },
                  {
                      label: t('admin:reviewRequired'),
                      id: 'risk-review-required',
                      field: 'reviewRequired',
                      minWidth: 160,
                      cells: { data: YesNoCell },
                      filterUi: GridColumnMenuActiveFilter,
                  },
                  {
                      label: t('ra:reviewFrequency'),
                      id: 'risk-review-frequency',
                      minWidth: 220,
                      field: '_reviewFrequency',
                      cells: { data: CustomizableCell((dataItem: any) => dataItem._reviewFrequency || '') },
                  },
                  {
                      label: t('admin:vendors'),
                      id: 'risk-vendors',
                      minWidth: 220,
                      field: 'vendor',
                      cells: {
                          data: ActiveInactiveCell(
                              (dataItem: any) => dataItem?.active === false,
                              (dataItem: any) => dataItem?.name,
                              true,
                              'vendors',
                          ),
                      },
                  },
                  {
                      label: t('admin:countries'),
                      id: 'risk-countries',
                      minWidth: 220,
                      field: 'country',
                      cells: {
                          data: ActiveInactiveCell(
                              (dataItem: any) => !countryCodes.includes(dataItem?.code),
                              (dataItem: any) => dataItem?.name,
                              true,
                              'countries',
                          ),
                      },
                  },
                  ...(isChild
                      ? [
                            {
                                label: t('active'),
                                id: 'risk-active',
                                field: 'active',
                                minWidth: 140,
                                cells: { data: YesNoCell },
                                filterUi: GridColumnMenuActiveFilter,
                            },
                        ]
                      : []),
              ]),
        // These columns never change
        {
            label: t('ra:createdBy'),
            field: '_createdByString',
            id: 'risk-createdby',
            minWidth: 170,
        },
        {
            label: t('ra:createdDate'),
            field: 'created',
            id: 'risk-created',
            minWidth: 180,
            cells: { data: DateCell },
            filter: 'date',
            filterUi: GridColumnDateFilter,
        },
        {
            label: t('ra:updatedBy'),
            field: '_updatedByString',
            id: 'risk-updatedby',
            minWidth: 180,
        },
        {
            label: t('ra:updatedDate'),
            field: 'updated',
            id: 'risk-updated',
            minWidth: 180,
            cells: { data: DateCell },
            filter: 'date',
            filterUi: GridColumnDateFilter,
        },
        {
            field: 'recordId',
            id: 'risks-id',
            showByDefault: false,
            label: 'ID',
            filter: 'numeric',
            minWidth: 120,
        },
    ];

    return (
        <ExpandableGridV2
            title={title}
            isChildGrid={isChild}
            customKey="admin-risks-grid"
            id="admin-risks-grid"
            rowActions={rowActions}
            groupable={!isChild}
            showPager={showPager}
            hideCheckbox
            scrollable="scrollable"
            maxHeight={height}
            reorderable
            pageable={false}
            resizable
            items={items}
            extraMenuItems={extraMenuItems}
            className={className}
            detail={(risk: any) => {
                if (risk?.associatedControls?.length && showDetail) {
                    return (
                        <AdminControlsChildGrid
                            isNested
                            key="control-admin-grid-general"
                            associatedControls={risk.associatedControls}
                            onUnassign={() => {}}
                            risk={risk}
                            rowActions={(item: any) => [
                                {
                                    label: 'ra:editControlLabel',
                                    icon: ['fal', 'pen-to-square'],
                                    onClick: () => {
                                        editControl(item);
                                    },
                                    enabled: canEdit,
                                },
                                {
                                    label: 'ra:unassignControl',
                                    icon: ['fal', 'link-slash'],
                                    onClick: () => {
                                        unassignControl(risk, item);
                                    },
                                    enabled: canEdit,
                                },
                            ]}
                            isSystemLevel={isSystemLevel}
                        />
                    );
                }
                return null;
            }}
            columns={columns}
        />
    );
};

export default AdminRisksGrid;
